import { Box, Stack, useMediaQuery, useTheme } from '@mui/material';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import CAppBreadcrumbs from '../../../app/components/CAppBreadcrumbs';
import CAppSubTitle from '../../../app/components/CAppSubTitle';
import CAppTitle from '../../../app/components/CAppTitle';
import AppCustomIcon from '../../../app/features/icon/AppIcon';
import AppLoaderPage from '../../../app/features/loader/page/AppLoaderPage';
import HAppRoutes from '../../../app/features/routes/helpers/HAppRoutes';
import { useAuthContext } from '../../auth/contexts/AuthContext';
import CInvestDisclaimer from '../../investor/components/CInvestDisclaimer';
import DocumentRoutes from '../DocumentRoutes';
import FDocumentFilters from '../features/filters/FDocumentFilters';
import HDocumentFile from '../helpers/HDocumentFile';
import useDocumentFiles from '../hooks/useDocumentFiles';
import IDocumentFile from '../interfaces/IDocumentFile';
import IDocumentsFilters from '../interfaces/IDocumentsFilters';
import langDocumentFiles from '../langs/langDocumentFiles';
import VDocumentFilesTable from '../views/VDocumentFilesTable';

const PDocumentCompany = () => {
	const authContext = useAuthContext();
	const { t } = useTranslation();

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const { documents } = useDocumentFiles();

	const [filters, setFilters] = useState<IDocumentsFilters>({
		fundIds: null,
		documentType: null,
		releasePeriod: null,
		format: null,
		fundType: null,
		fundVintage: null,
	});

	const investExpertise = 'company';

	const [documentsForFund, setDocumentsForFund] = useState<IDocumentFile[] | null>(null);
	useEffect(() => {
		if (documents) {
			let documentsTmp = _.cloneDeep(documents);
			documentsTmp = documentsTmp.filter((x) => x.company && !x.fund);
			setDocumentsForFund(documentsTmp);
		}
	}, [documents]);

	const [documentsFiltered, setDocumentsFiltered] = useState<IDocumentFile[] | null>(null);
	useEffect(() => {
		if (documentsForFund) {
			let documentsTmp = _.cloneDeep(documentsForFund);
			if (filters.keywords) {
				const searchWords = filters.keywords.split(' ');
				searchWords.forEach((word) => {
					documentsTmp = documentsTmp.filter((x) =>
						HDocumentFile.cleanWord(x.name).includes(HDocumentFile.cleanWord(word)),
					);
				});
			}
			if (filters.documentType) {
				documentsTmp = documentsTmp.filter((x) => x.documentType === filters.documentType);
			}
			if (filters.format) {
				documentsTmp = documentsTmp.filter((x) => x.formatTag === filters.format);
			}
			if (filters.releasePeriod) {
				switch (filters.releasePeriod) {
					case '1_month':
						documentsTmp = documentsTmp.filter(
							(x) => moment(x.date).diff(moment().subtract(1, 'month'), 'days') > 0,
						);
						break;
					case '3_months':
						documentsTmp = documentsTmp.filter(
							(x) => moment(x.date).diff(moment().subtract(3, 'month'), 'days') > 0,
						);
						break;
					case '6_months':
						documentsTmp = documentsTmp.filter(
							(x) => moment(x.date).diff(moment().subtract(6, 'month'), 'days') > 0,
						);
						break;
					case '1_year':
						documentsTmp = documentsTmp.filter(
							(x) => moment(x.date).diff(moment().subtract(1, 'year'), 'days') > 0,
						);
						break;
					default:
						null;
				}
			}
			setDocumentsFiltered(documentsTmp);
		}
	}, [documentsForFund, filters]);

	return (
		<>
			{investExpertise && documentsForFund ? (
				<>
					<Helmet>
						<title>
							{authContext.authContext === 'employee' ? '[E] ' : ''} {t(langDocumentFiles.title.tag)} -
							{authContext.currentCompany ? authContext.currentCompany.name : ''}
						</title>
					</Helmet>
					<CAppBreadcrumbs
						items={[
							{
								label: t(langDocumentFiles.title.tag),
								location: HAppRoutes.getPath(DocumentRoutes.dashboard.tag),
							},

							{
								label: t(langDocumentFiles.company_documents.tag),
								location: HAppRoutes.getPath(DocumentRoutes.documents_company.tag),
							},
						]}
					/>
					<Stack
						direction={!isMobile ? 'row' : 'column'}
						alignItems={!isMobile ? 'flex-end' : 'stretch'}
						justifyContent="space-between"
					>
						<CAppTitle title={t(langDocumentFiles.company_documents.tag)} />
					</Stack>
					<Box sx={{ marginTop: '30px' }}>
						<FDocumentFilters
							orderFilters={[
								{
									filterTag: 'keywords',
									xs: 12,
									md: 4,
								},
								{
									filterTag: 'documentType',
									xs: 12,
									md: 4,
								},
								{
									filterTag: 'releasePeriod',
									xs: 12,
									md: 4,
								},
							]}
							allDocuments={documents ?? []}
							filters={filters}
							onChange={(newFilters) => setFilters(newFilters)}
						/>
					</Box>
					<CAppSubTitle
						title={String(
							t(langDocumentFiles.x_documents.tag, {
								count: documentsFiltered ? documentsFiltered.length : 0,
							}),
						)}
						icon={<AppCustomIcon tag="folders" />}
					/>

					<VDocumentFilesTable
						documents={documentsFiltered ?? []}
						displayColumns={['name', 'company.name', 'documentTypeName', 'date', 'id']}
					/>

					<CInvestDisclaimer />
				</>
			) : (
				<AppLoaderPage />
			)}
		</>
	);
};

export default PDocumentCompany;
